import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Calculator, Globe, DollarSign } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './ForeignCurrencyMortgageCalculatorPage/components/CalculationForm';
import ResultsOverview from './ForeignCurrencyMortgageCalculatorPage/components/ResultsOverview';
import PaymentSchedule from './ForeignCurrencyMortgageCalculatorPage/components/PaymentSchedule';
import QuickAdjustmentSidebar from './ForeignCurrencyMortgageCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './ForeignCurrencyMortgageCalculatorPage/components/ComparisonSidebar';
import ForecastSidebar from './ForeignCurrencyMortgageCalculatorPage/components/ForecastSidebar';
import TipsSidebar from './ForeignCurrencyMortgageCalculatorPage/components/TipsSidebar';
import InfoContent from './ForeignCurrencyMortgageCalculatorPage/components/InfoContent';
import RiskAssessment from './ForeignCurrencyMortgageCalculatorPage/components/RiskAssessment';
import CurrencyConverter from './ForeignCurrencyMortgageCalculatorPage/components/CurrencyConverter';

// Определение начального состояния формы
export const initialFormData = {
  loanAmount: '10000000',
  currency: 'USD', // USD, EUR, CNY, etc.
  equivalentInRubles: '900000', // Автоматически рассчитывается
  loanTerm: '15', // Years
  interestRate: '4.5', // Annual percentage
  paymentType: 'annuity', // 'annuity' or 'differentiated'
  startDate: new Date().toISOString().split('T')[0],
  loanPurpose: 'mortgage',
  includeEarlyRepayment: false,
  earlyRepaymentType: 'reduction', // 'reduction' (term) or 'payment' (monthly payment)
  earlyRepaymentAmount: '1000', // в валюте кредита
  earlyRepaymentPeriod: 'monthly', // 'monthly', 'quarterly', 'semi_annually', 'annually', 'once'
  earlyRepaymentStartMonth: '6', // Month number (1-based)
  includeFees: false,
  initialFee: '500', // One-time initial fee in currency
  monthlyFee: '0', // Monthly service fee or insurance in currency
  monthlyFeeType: 'fixed', // 'fixed' or 'percent' (of remaining balance)
  initialDownPayment: '3000000', // В рублях
  hasIncomeInForeignCurrency: false, // Наличие дохода в валюте кредита
  monthlyIncomeRUB: '150000', // Ежемесячный доход в рублях
  monthlyIncomeForeign: '0', // Ежемесячный доход в валюте кредита
  currencyRiskTolerance: 'medium', // 'low', 'medium', 'high'
  exchangeRateScenario: 'moderate', // 'optimistic', 'moderate', 'pessimistic'
};

// Определение валют ипотеки
export const supportedCurrencies = [
  { id: 'USD', name: 'Доллар США', symbol: '$', defaultRate: 4.5, rateAgainstRUB: 90.0 },
  { id: 'EUR', name: 'Евро', symbol: '€', defaultRate: 4.0, rateAgainstRUB: 100.0 },
  { id: 'CHF', name: 'Швейцарский франк', symbol: 'Fr', defaultRate: 3.5, rateAgainstRUB: 105.0 },
  { id: 'CNY', name: 'Китайский юань', symbol: '¥', defaultRate: 5.0, rateAgainstRUB: 14.0 },
  { id: 'JPY', name: 'Японская иена', symbol: '¥', defaultRate: 2.5, rateAgainstRUB: 0.65 },
  { id: 'GBP', name: 'Фунт стерлингов', symbol: '£', defaultRate: 4.25, rateAgainstRUB: 115.0 },
];

// Определение периодов досрочного погашения
export const earlyRepaymentPeriods = [
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' },
  { id: 'once', name: 'Разово' }
];

// Определение сценариев изменения курса валют
export const exchangeRateScenarios = [
  { id: 'optimistic', name: 'Оптимистический', description: 'Укрепление рубля на 10% в год' },
  { id: 'moderate', name: 'Умеренный', description: 'Стабильный курс с инфляцией 4-5% в год' },
  { id: 'pessimistic', name: 'Пессимистический', description: 'Ослабление рубля на 15% в год' },
  { id: 'crisis', name: 'Кризисный', description: 'Резкие скачки курса до 30-50% в период кризиса' }
];

// Определение уровней толерантности к валютному риску
export const riskToleranceLevels = [
  { id: 'low', name: 'Низкая', description: 'Минимальный риск, консервативный подход' },
  { id: 'medium', name: 'Средняя', description: 'Умеренный риск с разумными мерами защиты' },
  { id: 'high', name: 'Высокая', description: 'Повышенный риск для получения лучших условий' }
];

/**
 * ForeignCurrencyMortgageCalculatorPage Component - Калькулятор валютной ипотеки с анализом рисков и прогнозами
 */
const ForeignCurrencyMortgageCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('mortgage-calculator') || {
    id: 'foreign-currency-mortgage-calculator',
    name: 'Калькулятор валютной ипотеки',
    metaTitle: 'Калькулятор валютной ипотеки | Gensio',
    metaDescription: 'Полный калькулятор валютной ипотеки: расчет ежемесячного платежа в рублях и валюте, оценка рисков колебания курса, построение графика платежей с учетом различных сценариев изменения валютного курса.',
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор валютной ипотеки | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор валютной ипотеки: расчет ежемесячного платежа в рублях и валюте, оценка рисков колебания курса, построение графика платежей с учетом различных сценариев изменения валютного курса.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [currencyRates, setCurrencyRates] = useState({});
  const [historicalRates, setHistoricalRates] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedLoans, setSavedLoans] = useState([]);
  const [showConverter, setShowConverter] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных кредитов из localStorage ---
  useEffect(() => {
    const savedLoansData = localStorage.getItem('savedCurrencyMortgages');
    if (savedLoansData) {
      try {
        setSavedLoans(JSON.parse(savedLoansData));
      } catch (e) {
        console.error("Error loading saved currency mortgages:", e);
      }
    }
  }, []);

  // --- Загрузка текущих курсов валют ---
  useEffect(() => {
    // В реальном приложении здесь был бы API-запрос
    const mockRates = {};
    supportedCurrencies.forEach(currency => {
      mockRates[currency.id] = currency.rateAgainstRUB;
    });
    setCurrencyRates(mockRates);
    
    // Генерация исторических данных для графика
    const mockHistorical = [];
    const today = new Date();
    for (let i = 365; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      const usdRate = 90 * (1 + Math.sin(i / 30) * 0.1); // Синусоида для наглядности
      const eurRate = 100 * (1 + Math.cos(i / 45) * 0.08);
      mockHistorical.push({
        date: date.toISOString().split('T')[0],
        USD: usdRate,
        EUR: eurRate,
        CHF: 105 * (1 + Math.sin(i / 60) * 0.07),
        CNY: 14 * (1 + Math.sin(i / 40) * 0.05),
        JPY: 0.65 * (1 + Math.cos(i / 50) * 0.06),
        GBP: 115 * (1 + Math.sin(i / 55) * 0.09)
      });
    }
    setHistoricalRates(mockHistorical);
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findCurrency = (id) => {
        const found = supportedCurrencies.find(c => c.id === id);
        return found ? found : supportedCurrencies[0];
      };

      switch (subToolId) {
        case 'usd':
          newState.currency = 'USD';
          newState.interestRate = findCurrency('USD').defaultRate.toString();
          break;
        case 'eur':
          newState.currency = 'EUR';
          newState.interestRate = findCurrency('EUR').defaultRate.toString();
          break;
        case 'cny':
          newState.currency = 'CNY';
          newState.interestRate = findCurrency('CNY').defaultRate.toString();
          break;
        case 'income-in-currency':
          newState.hasIncomeInForeignCurrency = true;
          newState.monthlyIncomeForeign = '2000';
          break;
        case 'high-risk':
          newState.currencyRiskTolerance = 'high';
          newState.exchangeRateScenario = 'pessimistic';
          break;
        case 'crisis-scenario':
          newState.exchangeRateScenario = 'crisis';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setRiskAnalysis(null);
    setErrors({});
    setShowFullSchedule(false);
    
  }, [subToolId]);

  // Сохранение кредита в localStorage
  const saveLoan = (name) => {
    if (!results) return;

    const newSavedLoan = {
      id: Date.now().toString(),
      name: name || `Ипотека ${formData.loanAmount} ${formData.currency} на ${formData.loanTerm} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      currency: formData.currency,
      riskAnalysis: { ...riskAnalysis }
    };

    const updatedSavedLoans = [...savedLoans, newSavedLoan];
    setSavedLoans(updatedSavedLoans);
    localStorage.setItem('savedCurrencyMortgages', JSON.stringify(updatedSavedLoans));

    return newSavedLoan;
  };

  // Удаление сохраненного кредита
  const deleteSavedLoan = (id) => {
    const updatedSavedLoans = savedLoans.filter(loan => loan.id !== id);
    setSavedLoans(updatedSavedLoans);
    localStorage.setItem('savedCurrencyMortgages', JSON.stringify(updatedSavedLoans));
  };

  // Загрузка сохраненного кредита
  const loadSavedLoan = (id) => {
    const loanToLoad = savedLoans.find(loan => loan.id === id);
    if (loanToLoad) {
      setFormData(loanToLoad.formData);
      setResults(loanToLoad.results);
      setRiskAnalysis(loanToLoad.riskAnalysis);
      // График будет пересчитан при необходимости
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <div className="flex items-center">
                  <Globe className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                  <DollarSign className="w-6 h-6 text-indigo-600 dark:text-indigo-400 flex-shrink-0 -ml-2" />
                </div>
                {tool?.name || 'Калькулятор валютной ипотеки'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
              
              {/* Кнопка показа конвертера валют */}
              <button 
                onClick={() => setShowConverter(!showConverter)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                {showConverter ? 'Скрыть конвертер валют' : 'Показать конвертер валют'}
              </button>
              
              {/* Компонент конвертера валют */}
              {showConverter && (
                <div className="mt-4">
                  <CurrencyConverter 
                    currencyRates={currencyRates}
                    defaultFromCurrency={formData.currency}
                    supportedCurrencies={supportedCurrencies}
                  />
                </div>
              )}
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              setRiskAnalysis={setRiskAnalysis}
              supportedCurrencies={supportedCurrencies}
              earlyRepaymentPeriods={earlyRepaymentPeriods}
              exchangeRateScenarios={exchangeRateScenarios}
              riskToleranceLevels={riskToleranceLevels}
              currencyRates={currencyRates}
              historicalRates={historicalRates}
              saveLoan={saveLoan}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData}
                  currencyRates={currencyRates}
                />

                {/* Блок оценки рисков */}
                {riskAnalysis && (
                  <RiskAssessment 
                    riskAnalysis={riskAnalysis}
                    formData={formData}
                    currencyRates={currencyRates}
                  />
                )}
                
                <PaymentSchedule 
                  schedule={schedule} 
                  results={results}
                  formData={formData}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о валютной ипотеке --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              supportedCurrencies={supportedCurrencies}
              currencyRates={currencyRates}
            />

            {/* --- Панель прогнозирования курса --- */}
            <ForecastSidebar 
              formData={formData}
              historicalRates={historicalRates}
              currencyRates={currencyRates}
              supportedCurrencies={supportedCurrencies}
              exchangeRateScenarios={exchangeRateScenarios}
            />

            {/* --- Панель сравнения ипотек --- */}
            <ComparisonSidebar 
              savedLoans={savedLoans}
              deleteSavedLoan={deleteSavedLoan}
              loadSavedLoan={loadSavedLoan}
              currencyRates={currencyRates}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Payment Schedule Table */
        .payment-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .payment-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .payment-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .payment-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default ForeignCurrencyMortgageCalculatorPage;